import {
  isModelGone,
  modelPicker,
  type ModelName,
  type ModelOption,
  type ModelPicker,
  type SessionView,
} from '../data/models';
import {
  levelOffer,
  scaleLevelOf,
  shownLevel,
  type Level,
  type LevelOffer,
  type ThinkingChoice,
} from '../data/thinking-levels';
import type { ModelsResponse } from '../service/orchestrator-client';

import { useModels } from './use-models';

/** What the composer's Model chip, its picker and the Thinking chip and dial read. */
export interface ModelControls {
  /** The `/models` response in use; null until the first read answers. */
  models: ModelsResponse | null;
  /** A fresh read is out: the Thinking popover's pending row. */
  checking: boolean;
  /** Reads `/models` again (every opening of a popover). */
  refresh: () => void;
  /** The providers' groups and the shown model (modelPicker); null until `/models` answers. */
  picker: ModelPicker | null;
  /**
   * The chat's model, which a successful `/models` read no longer lists (isModelGone), named by
   * its stored name, else its id; null otherwise. Send waits until a model is picked.
   */
  unavailable: { id: string; label: string; groupId?: string } | null;
  /**
   * The model the chip names and whose levels the dial draws; null when none is shown, and
   * while the chat's model is unavailable.
   */
  shown: ModelOption | null;
  /** The shown model's levels (levelOffer); null until `/models` answers, and while unavailable. */
  offer: LevelOffer | null;
  /**
   * What the Thinking chip shows: the shown model's level for the chat's choice (shownLevel);
   * while the chat's model is unavailable, the scale's step for the choice (scaleLevelOf). Null
   * on a composer that keeps no level (no onThinkingChange: the Vault dock), so its rows read
   * "Up to {cap}" and a pick lowers nothing, and for a model with no levels.
   */
  level: Level | null;
  /** The chat's thinking choice, from Composer's `thinking` prop; null while none was chosen. */
  choice: ThinkingChoice | null;
}

/**
 * The one place the chips, the picker and the dial read the model and the level from: derived
 * once per composer from `GET /api/v1/models` and the chat's own fields. `thinking` undefined:
 * this composer keeps no level. `name`: the chat's model as picked, which names it once the
 * list no longer does.
 */
export function useModelControls(
  modelId: string,
  view: SessionView,
  thinking: { choice: ThinkingChoice | null } | undefined,
  name?: ModelName,
): ModelControls {
  const { models, checking, refresh } = useModels();
  const picker = models ? modelPicker(models, modelId, view) : null;
  const unavailable =
    models && isModelGone(models, modelId, view)
      ? { id: modelId, label: name?.label ?? modelId, groupId: name?.groupId }
      : null;
  const shown = unavailable ? null : (picker?.shown ?? null);
  const offer =
    models && !unavailable
      ? levelOffer(models.thinking_scale, shown ? shown.thinking : undefined)
      : null;
  const choice = thinking ? thinking.choice : null;
  const level = !thinking
    ? null
    : unavailable
      ? scaleLevelOf(models?.thinking_scale, choice)
      : offer
        ? shownLevel(offer, choice)
        : null;
  return { models, checking, refresh, picker, unavailable, shown, offer, level, choice };
}
